import classnames from 'classnames';
import * as React from 'react';

import { onKeyPress } from '../../utils/keypress';
import { getNextIndex } from '../../utils/navigation';

import styles from './index.module.css';

interface Props {
  value: string;
  name: string;
  containerClassName?: string;
  className?: string;
  placeholder?: string;
  ariaLabelledBy?: string;
  options: string[];
  onSelection: (name: string, value: string) => void;
  onChange: (newValue: string) => void;
}

interface State {
  isSelected: boolean;
  selectedIndex: number | undefined;
  possibilities: string[];
}

export class AutocompleteInput extends React.Component<Props, State> {
  private possibilitiesRef: React.MutableRefObject<HTMLLIElement | null>[] = [];
  private inputRef = React.createRef<HTMLInputElement>();

  constructor(props: Props) {
    super(props);
    this.state = {
      isSelected: false,
      selectedIndex: undefined,
      possibilities: [],
    };
  }

  handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value = '' } = event.currentTarget;
    this.props.onChange(value);
    this.setState(
      {
        isSelected: false,
        selectedIndex: undefined,
      },
      () => this.setState({ possibilities: this.getPossibilities(value) }),
    );
  };

  getPossibilities(value?: string): string[] {
    if (value && !this.state.isSelected) {
      return this.props.options.filter((option) => {
        return new RegExp(`(\\b${value.toLowerCase()}\\S*\\b)`, 'ig').test(
          option.toLowerCase(),
        );
      });
    }
    return this.props.options;
  }

  selectPossibility(possibility: string) {
    this.props.onChange(possibility);
    this.setState({ isSelected: true, selectedIndex: undefined }, () =>
      this.props.onSelection(this.props.name, possibility),
    );
  }

  navigateOptions = (step: number) => {
    const { possibilities, selectedIndex } = this.state;
    const nextIndex = getNextIndex(selectedIndex, step, possibilities.length);
    this.setState({ ...this.state, selectedIndex: nextIndex });
    if (nextIndex === undefined) return;
    this.possibilitiesRef[nextIndex].current?.focus();
  };

  handleKeyDown = (e: React.KeyboardEvent) => {
    if (this.state.possibilities.length) {
      if (e.key === 'Tab') {
        e.preventDefault();
      }
      onKeyPress({
        e,
        onConfirm: () =>
          this.state.selectedIndex !== undefined &&
          this.selectPossibility(
            this.state.possibilities[this.state.selectedIndex],
          ),
        onClose: () => {
          this.inputRef.current?.focus();
          this.props.onChange('');
          this.setState({
            isSelected: false,
            selectedIndex: undefined,
            possibilities: [],
          });
        },
        onNext: () => this.navigateOptions(1),
        onMoveDown: () => this.navigateOptions(1),
        onPrevious: () => this.navigateOptions(-1),
        onMoveUp: () => this.navigateOptions(-1),
      });
    }
  };

  renderPossibility(possibility: string, index: number) {
    return (
      <li
        ref={this.possibilitiesRef[index]}
        tabIndex={1}
        className={`${styles.Suggestion} ${
          index === this.state.selectedIndex ? styles.Selected : ''
        }`}
        key={possibility}
        onClick={() => this.selectPossibility(possibility)}
        onKeyDown={this.handleKeyDown}
      >
        {possibility}
      </li>
    );
  }

  renderPossibilities() {
    if (!this.state.isSelected && this.state.possibilities.length) {
      return (
        <ul
          id={`${
            this.props.ariaLabelledBy ? `${this.props.ariaLabelledBy}-` : ''
          }autocomplete-list`}
          className={styles.Suggestions}
        >
          {this.state.possibilities.map((possibility, index) => {
            if (!this.possibilitiesRef[index]) {
              this.possibilitiesRef.push(React.createRef<HTMLLIElement>());
            }
            return this.renderPossibility(possibility, index);
          })}
        </ul>
      );
    }
  }

  render() {
    const { className, name, placeholder, containerClassName } = this.props;
    return (
      <div className={classnames(styles.AutocompleteInput, containerClassName)}>
        <input
          ref={this.inputRef}
          name={name}
          className={className}
          value={this.props.value}
          onChange={this.handleInputChange}
          placeholder={placeholder}
          onKeyDown={this.handleKeyDown}
          tabIndex={1}
          aria-labelledby={this.props.ariaLabelledBy}
          autoComplete="off"
          role="combobox"
          aria-controls={`${
            this.props.ariaLabelledBy ? `${this.props.ariaLabelledBy}-` : ''
          }autocomplete-list`}
          aria-expanded={this.state.possibilities.length ? 'true' : 'false'}
          onFocus={() =>
            this.setState({ possibilities: this.getPossibilities() })
          }
        />
        {this.renderPossibilities()}
      </div>
    );
  }
}
